import { useQuery } from '@tanstack/react-query';

import { Barra } from '@/componentes/Esqueleto';
import { Medida } from '@/componentes/Medida';
import { janelaAteHoje } from '@/lib/dia-da-operacao';
import { formatarReais } from '@/lib/formato';
import { useSessao } from '@/modulos/sessao/consultas';

import { api } from './api';

/**
 * O custo por hora de UMA máquina nos últimos 30 dias — a célula "Custo/h" da
 * lista da frota, que a decisão 16 do dono mandou manter com o módulo que a
 * alimenta. Agora o módulo existe.
 *
 * Toda linha da lista pede a MESMA consulta, e o react-query a faz uma vez só.
 * Sem `custo.ler`, a célula diz isso em vez de pedir e receber 403.
 */
function ultimos30Dias(): { de: string; ate: string } {
  return janelaAteHoje(30);
}

export function CustoPorHoraDaMaquina({ equipamentoId }: { readonly equipamentoId: string }) {
  const sessao = useSessao();
  const pode = (sessao.data?.permissoes ?? []).includes('custo.ler');
  const { de, ate } = ultimos30Dias();
  const custos = useQuery({
    queryKey: ['custos', de, ate],
    queryFn: () => api.equipamentos(de, ate),
    enabled: pode,
    staleTime: 5 * 60_000,
  });

  if (!pode) return <span className="txt-apoio text-tinta-secundaria">Sem acesso a custos</span>;
  if (custos.isPending) {
    /* Carregando (p.32) na forma da célula: o valor e a base embaixo, à
       direita — nunca "—", que parece um valor. Sem região de status: a
       célula se repete em cada linha da lista, e o aviso viria uma vez por
       máquina. */
    return (
      <span className="flex flex-col items-end gap-2 py-1">
        <Barra className="h-3 w-20" />
        <Barra className="h-2 w-16" />
        <span className="sr-only">carregando</span>
      </span>
    );
  }
  const linha = custos.data?.equipamentos.find((e) => e.equipamentoId === equipamentoId);
  if (!linha || linha.custoPorHora === null) {
    return <span className="txt-apoio text-tinta-secundaria">Sem hora apontada</span>;
  }
  return (
    <span className="flex flex-col items-end">
      <Medida valor={formatarReais(linha.custoPorHora)} unidade="/h" colado />
      <span className="txt-apoio text-tinta-apagada">{linha.semPreco > 0 ? 'parcial, 30 dias' : 'direto, 30 dias'}</span>
    </span>
  );
}
